import React, { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Field, Icon, Select, TextInput } from '@kontentino/ui';
import { faCheck } from '@fortawesome/pro-regular-svg-icons/faCheck';
import ValidationScheme from 'app/constants/validationSchemes';
import {
  InvitableRoleOption,
  useInvitableRoleOptions,
} from './useInvitableRoleOptions';
import { ValidUserRole } from './userRoles';
import { useTranslation } from 'react-i18next';

const InviteUserformSchema = z.object({
  first_name: z.string().min(1).max(100),
  last_name: z.string().min(1).max(100),
  email: ValidationScheme.Email,
  role: ValidationScheme.RequiredString(),
});

export type InviteUserFormState = z.infer<typeof InviteUserformSchema>;

export const defaultValues: InviteUserFormState = {
  first_name: '',
  last_name: '',
  email: '',
  role: '',
};

type InviteUserFormProps = {
  onSubmit: (data: InviteUserFormState) => void;
  isSubmitting: boolean;
  onValidityChange?: (isValid: boolean) => void;
  serverError?: string;
  initialRole?: ValidUserRole;
};

const InviteUserForm = ({
  onSubmit,
  isSubmitting,
  onValidityChange,
  serverError,
  initialRole,
}: InviteUserFormProps) => {
  const { t } = useTranslation();
  const filteredRoleOptions = useInvitableRoleOptions();

  const allowedInitialRole =
    initialRole &&
    filteredRoleOptions.some((option) => option.value === initialRole)
      ? initialRole
      : defaultValues.role;

  const {
    control,
    handleSubmit,
    watch,
    formState: { errors, isValid, isSubmitted },
    setError,
    clearErrors,
  } = useForm<InviteUserFormState>({
    defaultValues: { ...defaultValues, role: allowedInitialRole },
    resolver: zodResolver(InviteUserformSchema),
    mode: 'onChange',
  });
  const selectedRole = watch('role');

  useEffect(() => {
    onValidityChange?.(isValid);
  }, [isValid, onValidityChange]);

  useEffect(() => {
    if (serverError && isSubmitted) {
      setError('email', { message: serverError });
    } else {
      clearErrors('email');
    }
  }, [serverError, setError, clearErrors, isSubmitted]);

  return (
    <form
      id="invite-user-form"
      onSubmit={handleSubmit(onSubmit)}
      className="tw-flex tw-flex-col tw-gap-6"
    >
      <Field.Group>
        <Field.Label required>{t('inviteUserForm.role')}</Field.Label>
        <Controller
          name="role"
          control={control}
          render={({ field: { onChange, value, ...restField } }) => {
            const selectedOption =
              filteredRoleOptions.find((option) => option.value === value) ||
              null;
            return (
              <Select
                {...restField}
                value={selectedOption}
                onChange={(option) =>
                  onChange((option as InvitableRoleOption).value)
                }
                options={filteredRoleOptions}
                isDisabled={isSubmitting}
                getOptionValue={(option: InvitableRoleOption) => option.value}
                getOptionLabel={(option: InvitableRoleOption) => option.label}
                placeholder={t('inviteUserForm.selectRole')}
                isSearchable={false}
                menuPortalTarget={document.body}
                styles={{
                  menuPortal: (styles) => ({
                    ...styles,
                    zIndex: 1010,
                  }),
                }}
              />
            );
          }}
        />
        <Field.Error>
          {errors.role?.message && t(errors.role.message)}
        </Field.Error>
        {selectedRole && (
          <ul className="tw-mt-1 tw-flex tw-flex-col tw-gap-1">
            {(
              t(`userRoles.permissions.${selectedRole}`, {
                returnObjects: true,
              }) as string[]
            ).map((permission) => (
              <li
                key={permission}
                className="tw-flex tw-items-center tw-gap-2 tw-text-sm tw-text-grayscale-100"
              >
                <Icon
                  icon={faCheck}
                  className="tw-text-xs tw-text-grayscale-100"
                />
                {permission}
              </li>
            ))}
          </ul>
        )}
      </Field.Group>

      <Field.Group>
        <Field.Label required>{t('inviteUserForm.firstName')}</Field.Label>
        <Controller
          name="first_name"
          control={control}
          render={({ field, fieldState: { error } }) => (
            <TextInput
              {...field}
              error={!!error?.message}
              disabled={isSubmitting}
            />
          )}
        />
        <Field.Error>{errors.first_name?.message}</Field.Error>
      </Field.Group>

      <Field.Group>
        <Field.Label required>{t('inviteUserForm.lastName')}</Field.Label>
        <Controller
          name="last_name"
          control={control}
          render={({ field, fieldState: { error } }) => (
            <TextInput
              {...field}
              error={!!error?.message}
              disabled={isSubmitting}
            />
          )}
        />
        <Field.Error>{errors.last_name?.message}</Field.Error>
      </Field.Group>

      <Field.Group>
        <Field.Label required>{t('inviteUserForm.email')}</Field.Label>
        <Controller
          name="email"
          control={control}
          render={({ field, fieldState: { error } }) => (
            <TextInput
              {...field}
              type="email"
              error={!!error?.message}
              disabled={isSubmitting}
            />
          )}
        />
        <Field.Caption>{t('inviteUserForm.emailHelper')}</Field.Caption>
        <Field.Error>
          {errors.email?.message && t(errors.email.message)}
        </Field.Error>
      </Field.Group>
    </form>
  );
};

export default InviteUserForm;
